import { ChangeEvent, useCallback, useMemo, useRef } from 'react';
import { useUploadProfilePicture } from '@sib/api';
import { PROFILE_PICTURE_ACCEPTED_FILES } from '@sib/config';

interface UseAvatarUploadProps {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  onPending?: () => void;
}

export const useAvatarUpload = ({
  onSuccess,
  onError,
  onPending,
}: UseAvatarUploadProps = {}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    mutateAsync: uploadProfilePicture,
    isPending: isLoading,
    data,
  } = useUploadProfilePicture({
    onSuccess: () => {
      onSuccess?.();
    },
    onError: error => {
      console.error(error);
      onError?.(error);
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
  });

  const acceptFiles = useMemo(
    () => Object.values(PROFILE_PICTURE_ACCEPTED_FILES).flat().join(','),
    []
  );

  const uploadFile = useCallback(
    async (fileItem: File) => {
      onPending?.();
      await uploadProfilePicture({ file: fileItem });
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [uploadProfilePicture, onPending]
  );

  const handleFileChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
      uploadFile(file);
    },
    [uploadFile]
  );

  const openFileInput = () => fileInputRef?.current?.click();

  return {
    handleFileChange,
    fileInputRef,
    acceptFiles,
    isLoading,
    openFileInput,
    data,
  };
};
